import { callAPI } from '../services/api.service';
import { CONFIG, StoryStepsEnum } from '../config/mainConfig';
import { JSX, RefObject } from 'react';
import { Toast } from '../components/Common/Toast';
import CryptoJS from 'crypto-js';
import { UserLocalStorageKeysEnum } from '../interfaces/user.interface';
import { HTTPStatusEnum } from '../interfaces/log.interface';
import { FirebaseTimestamp } from '../interfaces/_common.interface';
import { Timestamp } from 'firebase/firestore';

type FirebaseDateLike = Timestamp | FirebaseTimestamp | Date | number | string | null | undefined;

export type StoryAge = '0-2_years' | '3-6_years' | '7-9_years' | '10-12_years' | '13-14_years';

export interface getStoryWordsCountResponse {
  min: number;
  max: number;
}

export const getStoryWordsCount = (age: StoryAge): getStoryWordsCountResponse => {
  const wordRanges = {
    '0-2_years': { min: 0, max: 50 },
    '3-6_years': { min: 500, max: 1000 },
    '7-9_years': { min: 2000, max: 4000 },
    '10-12_years': { min: 3000, max: 5000 },
    '13-14_years': { min: 4000, max: 6000 },
  };

  return wordRanges[age] || { min: 0, max: 0 };
};

export const generatePDF = async (bookId: string, isShare?: boolean): Promise<string | null> => {
  try {
    const queryParams = getURLSearchParams();
    const response = !isShare
      ? await callAPI(`api/pdf-generator/pdf?bookId=${bookId}`)
      : await callAPI(`api/pdf-generator/public/pdf?bookId=${bookId}`, null, 'GET', {
          share: queryParams.get(CONFIG.HEADERS.BOOK_SHARE),
        });
    return response?.pdfUrl;
  } catch (error) {
    return null;
  }
};

export const encryptToken = (token: string, secretKey: string): string => {
  // Generate a random IV (16 bytes for AES)
  const iv = CryptoJS.lib.WordArray.random(16);

  // Encrypt the token with AES using the secret key and IV
  const encryptedData = CryptoJS.AES.encrypt(token, secretKey, { iv }).toString();

  // Convert IV to base64 string
  const ivString = CryptoJS.enc.Base64.stringify(iv);

  // Prepend IV to the encrypted data, separated by a delimiter (you can choose your own delimiter)
  const encryptedTokenWithIV = ivString + '|' + encryptedData;

  return encryptedTokenWithIV;
};

// todo: ensure that the `ts` as a Date and set Interface
// Accepts every timestamp shape the API serializes: Firestore {_seconds,_nanoseconds} /
// {seconds,nanoseconds}, Timestamp-like objects with toDate(), ISO strings, Date instances,
// and epoch millis. Returns '' for null/undefined/unparseable values.
export const convertTimestampToDateTime = (ts: any, onlyDate = false): string => {
  if (ts == null) return '';

  let timestamp: Date | null = null;
  if (ts instanceof Date) {
    timestamp = ts;
  } else if (typeof ts === 'number' || typeof ts === 'string') {
    timestamp = new Date(ts);
  } else if (typeof ts === 'object') {
    const seconds = ts._seconds ?? ts.seconds;
    if (typeof seconds === 'number') {
      const nanos = ts._nanoseconds ?? ts.nanoseconds ?? 0;
      timestamp = new Date(seconds * 1000 + nanos / 1000000);
    } else if (typeof ts.toDate === 'function') {
      timestamp = ts.toDate();
    }
  }

  if (!timestamp || isNaN(timestamp.getTime())) return '';

  if (onlyDate) {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const month = monthNames[timestamp.getMonth()];
    const day = timestamp.getDate();
    const year = timestamp.getFullYear();

    return `${month} ${day}, ${year}`;
  } else {
    return timestamp.toLocaleString();
  }
};

export function isFirebaseDateInFuture(value: FirebaseDateLike): boolean {
  if (value == null) return false;

  let date: Date | null = null;

  if (value instanceof Date) {
    date = value;
  } else if (value instanceof Timestamp) {
    date = value.toDate();
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else if (typeof value === 'string') {
    date = new Date(value);
  } else if (typeof value === 'object') {
    const v = value as any;
    if (typeof v.seconds === 'number' || typeof v._seconds === 'number') {
      const seconds = v.seconds ?? v._seconds;
      const nanos = v.nanoseconds ?? 0;
      date = new Date(seconds * 1000 + Math.floor(nanos / 1e6));
    }
  }

  if (!date || isNaN(date.getTime())) return false;
  return date.getTime() > Date.now();
}

export function convertTimestampToDateTimeString(
  timestamp: string | number | Date | FirebaseTimestamp | null | undefined,
  isFullTime?: boolean,
): string {
  if (!timestamp) return '';

  let date: Date;

  if (typeof timestamp === 'string' || typeof timestamp === 'number' || timestamp instanceof Date) {
    date = new Date(timestamp);
  } else {
    // Handle Firebase timestamp - convert UTC timestamp to local date preserving calendar day
    const utcDate = new Date((timestamp._seconds || 0) * 1000);

    // Extract UTC components to preserve the intended calendar day
    const year = utcDate.getUTCFullYear();
    const month = utcDate.getUTCMonth();
    const day = utcDate.getUTCDate();

    // Create local date with same calendar day (set to noon to avoid DST issues)
    date = new Date(year, month, day, 12, 0, 0, 0);
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  if (isFullTime) {
    console.log(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  console.log(`${year}-${month}-${day}T${hours}:${minutes}`);
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function mapDateToLocalString(date: FirebaseTimestamp | Date | null | undefined) {
  if (!date) return 'Date not available yet';
  const localDate = new Date(convertTimestampToDateTimeString(date));
  console.log(localDate);
  return localDate.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
}

export function getDateFromFullDate(initialDate: Date | string, showYear?: boolean): string | null {
  if (!initialDate) return null;
  const date = new Date(initialDate);

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const month = monthNames[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');

  if (showYear) {
    return `${month} ${day}, ${date.getFullYear()}`;
  }

  return `${month} ${day}`;
}

export function isToday(dateToCheck: Date): boolean {
  const now = new Date();
  return (
    now.getFullYear() === dateToCheck.getFullYear() &&
    now.getMonth() === dateToCheck.getMonth() &&
    now.getDate() === dateToCheck.getDate()
  );
}

export function getDateAfter(currentDate: Date = new Date(), days: number = 1): Date {
  currentDate.setDate(currentDate.getDate() + days); // Add the number of days
  return currentDate;
}

export const goToTopOfPage = (scrollElement = window): void => {
  scrollElement.scrollTo(0, 0);
};

export const scrollToArea = (targetRefs: RefObject<any>, id?: string | null): void => {
  if (!id) {
    return;
  }
  //Make Sure timeout is important
  setTimeout(() => {
    const targetDiv = targetRefs.current[id];
    if (targetDiv) targetDiv.scrollIntoView({ behavior: 'auto' });
  }, 100);
};

export const getCurrentYear = (): number => {
  const currentYear = new Date().getFullYear();
  return currentYear;
};

export const setCurrentStoryStep = (step: StoryStepsEnum | null): void => {
  localStorage.setItem(UserLocalStorageKeysEnum.STORY_STEP, step!);
};

export const makeDropdownOptions = (options: Array<{ label: string; value: string }>): JSX.Element[] => {
  return options.map((option) => {
    return <option key={option.value} value={option.value}>{option.label}</option>;
  });
};

export const getCurrentTimestamp = () => {
  const now = new Date();
  const seconds = Math.floor(now.getTime() / 1000);
  const nanoseconds = now.getMilliseconds() * 1e6;

  return {
    _seconds: seconds,
    _nanoseconds: nanoseconds,
  };
};

export const calculatingTimeFromTo = (createdDate: string, finishedDate: string): string => {
  if (!createdDate || !finishedDate) {
    return '';
  }
  const createdMs = new Date(createdDate).getTime();
  const finishedMs = new Date(finishedDate).getTime();

  if (finishedMs < createdMs) {
    throw new Error('Finished date is before created date');
  }

  const diffMs = finishedMs - createdMs;

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${minutes}m ${seconds}s`;
};

export const replaceFromString = (target: string, replaceItems: string): string => {
  const escapedReplaceItems = replaceItems.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

  // Create a regular expression that matches any of the characters in replaceItems
  const regex = new RegExp(`[${escapedReplaceItems}]`, 'g');

  // Replace all matches with a space
  return target.replace(regex, ' ');
};

export const capitalizeFirstCharacter = (str: string): string => {
  if (!str) return str; // If the string is empty or null, return it as is.
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const secondsToMinutesAndSeconds = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return `${formattedMinutes}:${formattedSeconds}`;
};

export const convertMidjourneyTimestampToDateTime = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);

    // Extract the parts of the date
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed
    const year = date.getUTCFullYear();

    // Format the date as MM:HH dd/mm/yyyy
    return `${hours}: ${minutes} ${day}/${month}/${year}`;
  } catch (error) {
    return '';
  }
};

export const localDateToUTCKeepSame = (localDate: Date): Date => {
  if (!localDate || !(localDate instanceof Date) || isNaN(localDate.getTime())) {
    throw new Error('Invalid date');
  }
  return new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 12, 0, 0, 0));
};

export const toStartOfLocalDay = (dateStr: string | Date): Date => {
  if (dateStr instanceof Date) {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // Prefer parsing explicit Y-M-D to avoid UTC parsing quirks of new Date('YYYY-MM-DD')
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/;
  const m = ymd.exec(dateStr.trim());
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1; // 0-based
    const d = Number(m[3]);
    return new Date(y, mo, d, 0, 0, 0, 0); // ← local time
  }

  // Fallback: parse any other string, then clamp to start of that local day
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) throw new Error(`Invalid date string: ${dateStr}`);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 0, 0, 0, 0);
};

export function getBrowserCountryCode(): string | null {
  try {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale; // e.g. "en-US"
    const countryCode = locale.split('-')[1]?.toUpperCase(); // → "US"
    console.log(locale);
    console.log(countryCode);
    console.log(new Date());
    return countryCode || null;
  } catch {
    return null;
  }
}

export async function getCountryFromIP(): Promise<string | null> {
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();
    return data?.country || null; // e.g. "US", "PL"
  } catch {
    return null;
  }
}

export const handleContinueBookInFreeMode = async (bookId: string) => {
  try {
    const response = await callAPI(`api/book-update/make-book-free-relax-mode`, { bookId });
    if (response.status === HTTPStatusEnum.OK) {
      Toast('Relax Mode Activated!');
    }
    return response;
  } catch (error) {
    return null;
  }
};

// Custom deep equality checker
export const isEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2?.includes(key) || !isEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

export const cleanObject = <T extends Record<any, any>>(obj: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined),
  ) as Partial<T>;
};

export const moveToTopPage = (): void => {
  window.scrollTo(0, 0);
};

export const getURLSearch = (useHashParams?: boolean): string => {
  const mainRouteArray = !useHashParams
    ? window.location.href?.split('#')[0]?.split('?')
    : window.location.href?.split('#')[1]?.split('?');
  return mainRouteArray?.length > 1 ? mainRouteArray[1] : '';
};

export const getURLSearchParams = (useHashParams?: boolean): URLSearchParams => {
  const queryString = getURLSearch(useHashParams);
  return new URLSearchParams(queryString);
};

export function zipCodeMaskToRegex(maskData: string | string[]): RegExp {
  const masks = Array.isArray(maskData) ? maskData : [maskData];

  const patternAlternatives = masks.map((mask) => {
    let pattern = '';
    for (const char of mask) {
      if (char === '9') {
        pattern += '[0-9]';
      } else if (char === 'A') {
        pattern += '[A-Za-z]';
      } else if (char === ' ') {
        pattern += '\\s';
      } else if (/[-/\\^$*+?.()|[\]{}]/.test(char)) {
        pattern += '\\' + char;
      } else {
        pattern += char;
      }
    }
    return pattern;
  });

  const regexStr = `^(${patternAlternatives.join('|')})$`;
  return new RegExp(regexStr);
}

export function isOdd(number: number): boolean {
  return number % 2 !== 0;
}
